import { useState, useEffect, useCallback } from "react";
import { API, useAuth } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import StatusBadge from "@/components/StatusBadge";
import { useNavigate } from "react-router-dom";
import {
  Users, DollarSign, FileText, AlertTriangle, Clock, Copy, CreditCard, TrendingUp
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

const formatCurrency = (val) => {
  if (val == null) return "$0.00";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(val);
};

const formatDate = (d) => {
  if (!d) return "\u2014";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const formatDateTime = (d) => {
  if (!d) return "\u2014";
  return new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

function KpiCard({ label, value, icon: Icon, color, testId }) {
  return (
    <Card className="kpi-card" data-testid={testId}>
      <CardContent className="pt-0 pb-0">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
            <p className="font-display text-2xl font-semibold mt-1 tabular-nums">{value}</p>
          </div>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
      </CardContent>
    </Card>
  );
}

function RecentPaymentsTable({ payments, navigate }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold">Recent Payments</CardTitle>
          <Button variant="ghost" size="sm" onClick={() => navigate("/payments")} data-testid="view-all-payments">View All</Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Time</TableHead>
              <TableHead>Dealer</TableHead>
              <TableHead className="text-right">Gross</TableHead>
              <TableHead className="text-right">Invoice</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(payments || []).map((p) => (
              <TableRow key={p.id} className="table-row-hover cursor-pointer" onClick={() => navigate("/payments")}>
                <TableCell className="text-xs text-muted-foreground">{formatDateTime(p.platform_received_at)}</TableCell>
                <TableCell className="font-medium text-sm">{p.dealer_name}</TableCell>
                <TableCell className="text-right tabular-nums text-sm">{formatCurrency(p.gross_amount)}</TableCell>
                <TableCell className="text-right tabular-nums text-sm">{formatCurrency(p.invoice_amount)}</TableCell>
                <TableCell><StatusBadge status={p.is_duplicate ? "duplicate" : p.processing_status} /></TableCell>
              </TableRow>
            ))}
            {(!payments || payments.length === 0) && (
              <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-8">No payments received yet</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function RecentInvoicesTable({ invoices, navigate }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-semibold">Recent Invoices</CardTitle>
          <Button variant="ghost" size="sm" onClick={() => navigate("/invoices")} data-testid="view-all-invoices">View All</Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Invoice #</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Due Date</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(invoices || []).map((inv) => (
              <TableRow key={inv.id} className="table-row-hover cursor-pointer" onClick={() => navigate("/invoices")}>
                <TableCell className="font-mono text-xs">{inv.stripe_invoice_number}</TableCell>
                <TableCell className="text-right tabular-nums text-sm">{formatCurrency(inv.invoice_amount)}</TableCell>
                <TableCell className="text-sm">{formatDate(inv.due_date)}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <StatusBadge status={inv.status} />
                    {inv.overdue_flag && inv.status !== "paid" && <StatusBadge status="overdue" />}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {(!invoices || invoices.length === 0) && (
              <TableRow><TableCell colSpan={4} className="text-center text-sm text-muted-foreground py-8">No invoices yet</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export default function Overview() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dashboard/overview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4,5,6,7,8].map((key) => (
            <Card key={`skeleton-${key}`} className="animate-pulse"><CardContent className="pt-6"><div className="h-10 bg-muted rounded" /></CardContent></Card>
          ))}
        </div>
      </div>
    );
  }

  const kpis = data?.kpis || {};
  const kpiCards = [
    { label: "Active Dealers", value: kpis.total_active_dealers || 0, icon: Users, color: "text-teal-600" },
    { label: "Gross This Month", value: formatCurrency(kpis.gross_payments_this_month), icon: DollarSign, color: "text-emerald-600" },
    { label: "Invoiced This Month", value: formatCurrency(kpis.invoiced_this_month), icon: FileText, color: "text-sky-600" },
    { label: "Paid Invoices", value: kpis.paid_invoices || 0, icon: CreditCard, color: "text-emerald-600" },
    { label: "Unpaid Invoices", value: kpis.unpaid_invoices || 0, icon: Clock, color: "text-amber-600" },
    { label: "Overdue Invoices", value: kpis.overdue_invoices || 0, icon: AlertTriangle, color: "text-rose-600" },
    { label: "Duplicate Alerts", value: kpis.duplicate_alerts || 0, icon: Copy, color: "text-slate-600" },
    { label: "Missing Payments", value: kpis.missing_monthly_payments || 0, icon: TrendingUp, color: "text-orange-600" },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight" data-testid="overview-title">Dashboard</h1>
        <p className="text-sm text-muted-foreground mt-1">Billing operations overview</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map((kpi) => (
          <KpiCard key={kpi.label} {...kpi} testId={`overview-kpi-${kpi.label.toLowerCase().replace(/\s+/g, "-")}`} />
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        <div className="xl:col-span-8 space-y-6">
          <RecentPaymentsTable payments={data?.recent_payments} navigate={navigate} />
          <RecentInvoicesTable invoices={data?.recent_invoices} navigate={navigate} />
        </div>

        <div className="xl:col-span-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold">Unresolved Alerts</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate("/alerts")} data-testid="view-all-alerts">View All</Button>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-3">
                {(data?.recent_alerts || []).map((alert) => (
                  <div key={alert.id} className="flex items-start gap-3 p-3 rounded-lg bg-muted/40 border">
                    <AlertTriangle className={`w-4 h-4 mt-0.5 shrink-0 ${
                      alert.severity === "critical" ? "text-rose-500" :
                      alert.severity === "warning" ? "text-amber-500" : "text-sky-500"
                    }`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <StatusBadge status={alert.alert_type} />
                        <StatusBadge status={alert.severity} />
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{alert.message}</p>
                      <p className="text-[10px] text-muted-foreground mt-1">{formatDateTime(alert.created_at)}</p>
                    </div>
                  </div>
                ))}
                {(!data?.recent_alerts || data.recent_alerts.length === 0) && (
                  <div className="text-center text-sm text-muted-foreground py-8">No unresolved alerts</div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
